/**
 * Base Loader Infrastructure
 *
 * Provides common loading patterns, configuration management, progress tracking,
 * and error handling for all data-specific loader modules in the selective
 * loading system.
 */

import { supabase } from '@/integrations/supabase/client';



/**
 * Data loading configuration
 */
export interface LoadingConfig {
  // Training sessions
  journalDays: number; // Default: 90 days
  includeCompetitionSessions: boolean; // Default: true
  
  // Techniques
  includeAdminTechniques: boolean; // Default: true
  includeUserTechniques: boolean; // Default: true
  includePublicTechniques: boolean; // Default: false
  
  // Static data
  includeAllPositions: boolean; // Default: true
  includeUserGamePlans: boolean; // Default: true
  includePublicGamePlans: boolean; // Default: false
  includeAdminTemplates: boolean; // Default: true
  
  // User data
  includeUserProfile: boolean; // Default: true
  includeUserPreferences: boolean; // Default: true

  // Roadmap data
  includeRoadmapData: boolean; // Default: true
  includeAllBeltLevels: boolean; // Default: true

  // Referral data
  includeReferralData: boolean; // Default: true

  // Static assets
  includeStaticAssets: boolean; // Default: true
  includeLogos: boolean; // Default: true

  // Performance settings
  batchSize: number; // Default: 100
  maxConcurrentRequests: number; // Default: 3
}

/**
 * Loading progress information
 */
export interface LoadingProgress {
  stage: string;
  current: number;
  total: number;
  percentage: number;
  estimatedTimeRemaining?: number;
}

/**
 * Progress callback function type
 */
export type ProgressCallback = (progress: LoadingProgress) => void;

/**
 * Loading result breakdown
 */
export interface LoadingBreakdown {
  trainingSessions: number;
  techniques: number;
  userTechniques: number;
  positions: number;
  gamePlans: number;
  adminTemplates: number;
  userProfile: number;
  roadmapMacro: number;
  roadmapMeso: number;
  referralCodes: number;
  referrals: number;
}

/**
 * Loading operation result
 */
export interface LoadingResult {
  success: boolean;
  totalRecords: number;
  loadedRecords: number;
  errors: string[];
  duration: number;
  breakdown: LoadingBreakdown;
}

/**
 * Standard loader method result
 */
export interface LoaderResult {
  count: number;
  errors: string[];
}

/**
 * Extended loader result for multi-type operations
 */
export interface ExtendedLoaderResult {
  [key: string]: number | string[];
  errors: string[];
}

/**
 * Base Loader Class
 * 
 * Provides shared infrastructure for all data-specific loaders including
 * configuration management, progress tracking, error handling, and common
 * loading patterns.
 */
export abstract class BaseLoader {
  protected config: LoadingConfig;
  protected progressCallback?: ProgressCallback;

  /**
   * Default configuration values
   */
  protected static readonly DEFAULT_CONFIG: LoadingConfig = {
    journalDays: 90,
    includeCompetitionSessions: true,
    includeAdminTechniques: true,
    includeUserTechniques: true,
    includePublicTechniques: false,
    includeAllPositions: true,
    includeUserGamePlans: true,
    includePublicGamePlans: false,
    includeAdminTemplates: true,
    includeUserProfile: true,
    includeUserPreferences: true,
    includeRoadmapData: true,
    includeAllBeltLevels: true,
    includeReferralData: true,
    includeStaticAssets: true,
    includeLogos: true,
    batchSize: 100,
    maxConcurrentRequests: 3
  };

  constructor(config: Partial<LoadingConfig> = {}) {
    this.config = { ...BaseLoader.DEFAULT_CONFIG, ...config };
  }

  /**
   * Set progress callback for loading operations
   */
  setProgressCallback(callback: ProgressCallback): void {
    this.progressCallback = callback;
  }

  /**
   * Update loading configuration
   */
  updateConfig(updates: Partial<LoadingConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Get current configuration
   */
  getConfig(): LoadingConfig {
    return { ...this.config };
  }

  /**
   * Report loading progress to callback if set
   */
  protected reportProgress(stage: string, current: number, total: number): void {
    if (this.progressCallback) {
      const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
      this.progressCallback({
        stage,
        current,
        total,
        percentage
      });
    }
  }

  /**
   * Handle errors consistently across all loaders
   */
  protected handleError(error: unknown, context: string): string {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error(`❌ ${context}:`, errorMessage);
    return `${context}: ${errorMessage}`;
  }

  /**
   * Get authenticated user or throw error
   */
  protected async getAuthenticatedUser(): Promise<{ id: string; email?: string }> {
    // Get current user from Supabase auth (more reliable than cache)
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.user?.id) {
      throw new Error('No authenticated user found in session');
    }

    return {
      id: session.user.id,
      email: session.user.email || 'unknown'
    };
  }

  /**
   * Execute a loading operation with consistent error handling
   */
  protected async executeLoadingOperation<T>(
    operation: () => Promise<T>,
    context: string
  ): Promise<T | null> {
    try {
      return await operation();
    } catch (error) {
      console.error(`❌ ${context} failed:`, error);
      throw error;
    }
  }

  /**
   * Create a standard loader result
   */
  protected createLoaderResult(count: number, errors: string[] = []): LoaderResult {
    return { count, errors };
  }

  /**
   * Create an extended loader result for multi-type operations
   */
  protected createExtendedLoaderResult(results: Record<string, number>, errors: string[] = []): ExtendedLoaderResult {
    return { ...results, errors } as ExtendedLoaderResult;
  }

  /**
   * Abstract method that each loader must implement for their specific loading logic
   */
  abstract load(userId: string): Promise<LoaderResult | ExtendedLoaderResult>;

  /**
   * Optional method for loaders that support additional data loading
   */
  async loadAdditional?(userId: string, ...args: unknown[]): Promise<LoaderResult | ExtendedLoaderResult>;
}
